import styled, { css } from 'styled-components';
import colors from './colors';
import styledButtons from './buttons/styled';

type ColorsI = typeof colors;
type ColorType = keyof ColorsI;

interface BaseProps {
  color?: ColorType;
}

const getColorByProps: (props: BaseProps) => string = ({ color }) =>
  `color: ${color ? colors[color] : colors.black};`;

const getFillSvgByProps: (props: BaseProps) => string = ({ color }) =>
  `fill ${color ? colors[color] : colors.black}`;

export const commonTyping = css`
  font-size: 16px;
  line-height: 24px;
  font-style: normal;
  font-weight: normal;
  color: ${colors.black};
  background: ${colors.lightGrey};
  border: 1.5px solid ${colors.grey};
  font-family: Arial;
`;

export const baseTyping = css`
  font-family: 'futura-pt', sans-serif;
  font-style: normal;
  font-weight: 450;
`;

export const LinkParagraph = styled.a<BaseProps>`
  ${baseTyping}
  font-size: 16px;
  line-height: 24px;
  color: ${colors.blue};
  text-decoration: none;
  min-height: 24px;

  & svg {
    margin-right: 8px;
    min-width: 16px;
    width: 16px;
    min-height: 16px;
    height: 16px;

    path {
      ${(props) => getFillSvgByProps(props)}
    }
  }
`;

export const HeaderLarge = styled.h1<BaseProps>`
  ${baseTyping}
  ${(props) => getColorByProps(props)}

  font-size: 42px;
  line-height: 32px;
`;

export const Header = styled.h2<BaseProps>`
  ${baseTyping}
  ${(props) => getColorByProps(props)}

  margin-block-start: 0;
  margin-block-end: 0;
  font-size: 24px;
  line-height: 32px;
`;

export const ParagraphLarge = styled.p<BaseProps>`
  ${baseTyping}
  ${(props) => getColorByProps(props)}

  font-size: 18px;
  line-height: 24px;
`;

export const Paragraph = styled.p<BaseProps>`
  ${baseTyping}
  display: flex;
  align-items: center;
  ${(props) => getColorByProps(props)}

  margin-block-start: 0;
  margin-block-end: 0;
  font-size: 16px;
  line-height: 24px;

  & svg {
    margin-right: 8px;
    min-width: 16px;
    width: 16px;
    min-height: 16px;
    height: 16px;

    path {
      ${(props) => getFillSvgByProps(props)}
    }
  }
`;

export const ParagraphSmall = styled.p<BaseProps>`
  ${baseTyping}
  ${(props) => getColorByProps(props)}

  font-size: 14px;
  line-height: 24px;
`;

export const ParagraphSmallest = styled.p<BaseProps>`
  ${baseTyping}
  ${(props) => getColorByProps(props)}

  font-size: 12px;
  line-height: 16px;
`;

export const Label = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border-radius: 100px;
  min-width: 64px;

  ${baseTyping}
  font-weight: normal;
  font-size: 12px;
  line-height: 16px;
  font-family: 'futura-pt', sans-serif;
  color: ${colors.white};
  width: max-content;
  background-color: ${colors.red};

  & svg {
    margin: 0 5px 0 0;
    width: 16px;
    height: 16px;
  }
`;

export const ThinLabel = styled(Label)`
  padding: 0 8px;
`;

export const FatLabel = styled(Label)`
  padding: 4px 8px;
`;

export const OnlyIconLabel = styled(Label)`
  width: 16px;
  height: 16px;
  padding: 4px;
  min-width: 0;

  & svg {
    margin: 0;

    path {
      fill: ${colors.white};
    }
  }
`;

interface PropsI {
  check?: boolean;
}

export const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  opacity: 1;
  position: absolute;
  left: -9999px;
`;

export const LabelForCheckbox = styled.label<PropsI>`
  width: 24px;
  height: 24px;
  background: ${(props) => (props.check ? colors.green : colors.lightGrey)};
  transition-duration: 0.3s;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const typings = {
  commonTyping,
  HeaderLarge,
  Header,
  ParagraphLarge,
  Paragraph,
  ParagraphSmall,
  ParagraphSmallest,
  ThinLabel,
  FatLabel,
  OnlyIconLabel,
  Checkbox,
  LabelForCheckbox,
  ...styledButtons,
};

export default typings;
